import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { EntriesPerPage } from '../../../Component/Model/Table/Table';
import { DataTable } from '../../../Component/Common/Components/DataTable/DataTable';
import { OrganisationLinkedScorecard } from '../../../Component/Model/Dto/Organisation/OrganisationLinkedScorecard';
import { LinkedScorecardTableMapper } from './OrgnaisationLinkedScorecardTableColumns';
import { OrganisationLinkedScorecardService } from '../../../Component/Api/Organisation/OrganisationLinkedScorecardService';
import { ConfirmationDialog } from '../../../Component/Common/Components/ConfirmationDialog';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { OrganisationAtom } from '../../../State/Organisation.Atom';
import { OrganidationLinkedScorecardModal } from './OrganisationLinkScorecardModal';

export const OrganisationLinkedScorecards = () => {
	const [linkedScorecardsData, setLinkedScorecardsData] = useState<OrganisationLinkedScorecard.SearchDto[]>([]);
	const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 5 });
	const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
	const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [openLinkScorecardModal, setOpenLinkScorecardModal] = useState<boolean>(false);
	const [openUnlinkScorecardConfirmationModal, setOpenUnlinkScorecardConfirmationModal] = useState<boolean>(false);
	const [selectedLinkedScorecard, setSelectedLinkedScorecard] = useState<string | undefined>();
	const [selectedLinkedScorecardName, setSelectedLinkedScorecardName] = useState<string>('');
    const organisation = useRecoilValue(OrganisationAtom);

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    };

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    };

    const handleConfirmUnlink = (id: string, scorecardName: string) => {
        setSelectedLinkedScorecard(id);
        setOpenUnlinkScorecardConfirmationModal(true);
        setSelectedLinkedScorecardName(scorecardName);
    }

    const handleCloseConfirmation = () => {
        setOpenUnlinkScorecardConfirmationModal(false);
        setSelectedLinkedScorecard(undefined);
        setSelectedLinkedScorecardName('');

    }

    const handleCloseLinkScorecardModal = () => {
        setOpenLinkScorecardModal(false);
        fetchData();
    }

    const handleUnlink = async () => {
        const scorecardToUnlink: OrganisationLinkedScorecard.Dto = {
            scorecardId: selectedLinkedScorecard!,
            organisationId: organisation.OrganisationId
        };
        try {
            await OrganisationLinkedScorecardService.UnlinkScorecard(scorecardToUnlink);
            toast.success("Scorecard unlinked succesfully")
            fetchData();

        } catch (error) {
            console.log(error)
        } finally {
            handleCloseConfirmation();
        }
    }

    const fetchData = async () => {
        setIsLoading(true);
        const searchRequest: OrganisationLinkedScorecard.SearchRequest = {
            organisationId: organisation.OrganisationId,
            pageNumber: pageNumber,
            pageSize: entriesPerPage.defaultValue!
        };
        try {
            const searchResult = await OrganisationLinkedScorecardService.SearchLinkedScorecards(searchRequest);
            if (searchResult.data) {
                setLinkedScorecardsData(searchResult.data.results);
                setTotalItemsCount(searchResult.data.totalItemsCount);
                setFilteredItemCount(searchResult.data.filteredItemsCount);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }

    };
    
    useEffect(() => {
        fetchData();
    }, [pageNumber, entriesPerPage.defaultValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const bulkActions = [
        {
            label: "Link Scorecard",
            action: () => setOpenLinkScorecardModal(true)
        }
    ];
    const linkedScorecardsTableData = LinkedScorecardTableMapper(linkedScorecardsData, handleConfirmUnlink);
    
	return (
		<>
			<Typography variant='h4' style={{ paddingBottom: '20px' }}>
				Linked Scorecards
			</Typography>
			<Typography variant='body2' style={{ paddingBottom: '20px' }}>
				Configure which scorecards are linked to your organisation.
			</Typography>

			{openLinkScorecardModal && (
				<OrganidationLinkedScorecardModal openModal={openLinkScorecardModal} handleClose={handleCloseLinkScorecardModal} organisationId={organisation.OrganisationId} />
			)}

			<DataTable
				table={linkedScorecardsTableData}
				entriesPerPage={entriesPerPage}
				pageNumber={pageNumber}
				filteredItemsCount={filteredItemCount}
				totalItemsCount={totalItemsCount}
				isLoading={isLoading}
				canSearch={false}
				entriesPerPageChanged={entriesPerPageChanged}
				pageNumberChanged={pageNumberChanged}
                bulkActions={bulkActions}
			/>
            
            <ConfirmationDialog
                showDialog={openUnlinkScorecardConfirmationModal}
                handleAccept={handleUnlink}
                handleCancel={handleCloseConfirmation}
                heading={"Unlink Scorecard"}
                message={`Are you sure you want to unlink ${selectedLinkedScorecardName} from your organisation?`}
            />
		</>
	);
};
